import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { McpToken } from 'api';
import {
  getGetIntegrationMcpTokensQueryKey,
  useDeleteIntegrationMcpTokensUuid
} from 'api';

import { useOrganisationId } from 'hooks/context';

import { REVOKE_MCP_ERROR, revokeMcpSuccess } from './constants';

export const useRevokeMcpToken = () => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const mutation = useDeleteIntegrationMcpTokensUuid();
  // `mutation.isPending` is stale within the click's render, so a same-tick
  // double click would pass it — the ref blocks the second DELETE.
  const inFlightRef = useRef(false);

  const revoke = (token: McpToken, onSuccess: () => void) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    mutation.mutate(
      { organisationId, uuid: token.uuid },
      {
        onSettled: () => {
          inFlightRef.current = false;
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetIntegrationMcpTokensQueryKey(organisationId)
          });
          openNotification({
            kind: 'success',
            message: revokeMcpSuccess(token.name)
          });
          onSuccess();
        },
        onError: () =>
          openNotification({ kind: 'error', message: REVOKE_MCP_ERROR })
      }
    );
  };

  return { revoke, isPending: mutation.isPending };
};
